import { useTranslation } from 'react-i18next';
import {
  WEEKDAY_KEYS,
  chunkWeekDays,
  isSameDay,
  startOfDay,
  weekdayKeyIndex,
} from '@/utils/appointmentCalendarDates';
import type { OccupancyCell, OccupancyLevel } from '@/utils/appointmentOccupancy';
import { cn } from '@/utils/cn';

export type OccupancyStaffRow = {
  id: string;
  name: string;
  cells: OccupancyCell[];
};

const LEVEL_CLASSES: Record<OccupancyLevel, string> = {
  off: 'bg-ds-neutral-tint text-ds-muted',
  green: 'bg-ds-green-tint text-ds-green-ink',
  yellow: 'bg-ds-amber-tint text-ds-amber-ink',
  red: 'bg-ds-red-tint text-ds-red-ink',
};

export function occupancyLevelClass(level: OccupancyLevel): string {
  return LEVEL_CLASSES[level];
}

export function StaffOccupancyGrid({
  staff,
  days,
  selectedStaffId,
  onSelectStaff,
}: {
  staff: OccupancyStaffRow[];
  days: Date[];
  selectedStaffId?: string | null;
  onSelectStaff: (staffId: string, date: Date) => void;
}) {
  const { t, i18n } = useTranslation();
  const today = startOfDay(new Date());
  const weeks = chunkWeekDays(days);
  const locale = i18n.language === 'da' ? 'da-DK' : 'en-US';
  const weekFormatter = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
  });

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-separate border-spacing-x-1 border-spacing-y-1">
        <thead>
          <tr>
            <th
              rowSpan={2}
              className="sticky left-0 z-10 bg-ds-card px-2 py-1 text-left align-bottom text-xs font-medium text-ds-muted"
            >
              {t('referrals.occupancy_name_column')}
            </th>
            {weeks.map((week) => {
              const start = week[0];
              const end = week[week.length - 1];
              if (!start || !end) return null;
              return (
                <th
                  key={start.toISOString()}
                  colSpan={week.length}
                  className="px-1 pb-0.5 pt-1 text-center text-xs font-medium text-ds-muted"
                >
                  {`${start.getDate()}. – ${weekFormatter.format(end)}`}
                </th>
              );
            })}
          </tr>
          <tr>
            {days.map((day, index) => (
              <th
                key={day.toISOString()}
                className={cn(
                  'w-10 px-0.5 py-1 text-center text-[11px] font-medium',
                  index > 0 && index % 7 === 0 ? 'border-l border-ds-line' : '',
                  isSameDay(day, today)
                    ? 'text-ds-teal'
                    : 'text-ds-muted',
                )}
              >
                <div>{t(`appointments.weekdays_short.${WEEKDAY_KEYS[weekdayKeyIndex(day)]}`)}</div>
                <div className="font-semibold text-ds-text">{day.getDate()}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {staff.map((row) => (
            <tr key={row.id}>
              <td className="sticky left-0 z-10 whitespace-nowrap bg-ds-card px-2 py-1">
                <button
                  type="button"
                  onClick={() =>
                    onSelectStaff(row.id, days.find((day) => isSameDay(day, today)) ?? days[0] ?? today)
                  }
                  className={cn(
                    'rounded-md px-2 py-1.5 text-left text-sm font-medium transition-colors',
                    selectedStaffId === row.id
                      ? 'bg-ds-teal-tint text-ds-teal'
                      : 'text-ds-text hover:bg-ds-row-hover',
                  )}
                >
                  {row.name}
                </button>
              </td>
              {row.cells.map((cell, index) => (
                <td
                  key={cell.date.toISOString()}
                  className={cn(
                    'p-0',
                    index > 0 && index % 7 === 0 ? 'border-l border-ds-line' : '',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSelectStaff(row.id, cell.date)}
                    title={t('referrals.occupancy_cell_label', {
                      name: row.name,
                      day: t(`appointments.weekdays_short.${WEEKDAY_KEYS[weekdayKeyIndex(cell.date)]}`),
                      level: t(`referrals.occupancy_level_${cell.level}`),
                    })}
                    aria-label={t('referrals.occupancy_cell_label', {
                      name: row.name,
                      day: t(`appointments.weekdays_short.${WEEKDAY_KEYS[weekdayKeyIndex(cell.date)]}`),
                      level: t(`referrals.occupancy_level_${cell.level}`),
                    })}
                    className={cn(
                      'block h-9 w-full min-w-9 rounded-md',
                      occupancyLevelClass(cell.level),
                    )}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function OccupancyLegend() {
  const { t } = useTranslation();
  const items: OccupancyLevel[] = ['green', 'yellow', 'red', 'off'];
  return (
    <ul className="flex flex-wrap gap-3 text-xs text-ds-muted">
      {items.map((level) => (
        <li key={level} className="flex items-center gap-1.5">
          <span className={cn('h-3 w-3 rounded-sm', occupancyLevelClass(level))} />
          {t(`referrals.occupancy_legend_${level}`)}
        </li>
      ))}
    </ul>
  );
}
